import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Tooltip,
  Alert,
  Radio,
  Statistic,
  Typography,
} from "antd";
import ReactApexChart from "react-apexcharts";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  BarChartOutlined,
  LineChartOutlined,
  AreaChartOutlined,
  InfoCircleOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import { useGetPerformanceTrendsQuery } from "../../../Redux/service/fsm/FSMDashboard";
import Loader from "../../Helpers/Loader/Loader";
import trans from "../../../utils";
import SectionTitle from "./widgets/SectionTitle";
import NumbersCard from "./widgets/NumbersCard";

const { Title, Text } = Typography;

// Colors matching your dashboard style
const colorScheme = {
  primary: "#0f6936",
  primaryLight: "#16a34a",
  secondary: "#059669",
  warning: "#ea580c",
  info: "#0ea5e9",
  success: "#22c55e",
  neutral: "#64748b",
  light: "#f8fafc",
  dark: "#1e293b",
  surface: "#ffffff",
  border: "#e2e8f0",
  danger: "#dc2626",
};

export default function FSMPerformanceTrends({ refreshKey }) {
  const {
    data: trendsResponse,
    isLoading,
    error,
    refetch,
  } = useGetPerformanceTrendsQuery(
    { refreshKey },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    refetch();
  }, []);
  const [chartType, setChartType] = useState("line");
  const [selectedMetric, setSelectedMetric] = useState("all");

  if (isLoading) {
    return (
      <Card
        style={{
          borderRadius: "12px",
          border: `1px solid ${colorScheme.border}`,
          background: colorScheme.surface,
        }}
      >
        <div style={{ textAlign: "center", padding: "48px 24px" }}>
          <Loader />
          <Text
            style={{
              marginTop: "16px",
              color: colorScheme.neutral,
              fontSize: "14px",
              display: "block",
            }}
          >
            {trans("Loading performance trends...")}
          </Text>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        style={{
          borderRadius: "12px",
          border: `1px solid ${colorScheme.border}`,
        }}
      >
        <Alert
          message={trans("Failed to load performance trends")}
          type="error"
          showIcon
          style={{ borderRadius: "6px" }}
        />
      </Card>
    );
  }

  // Extract data from response (handle both old and new structure)
  const trendsData = trendsResponse?.data || trendsResponse || {};

  const {
    months = [],
    tasks = [],
    completions = [],
    serviceRequests = [],
    avgDurations = [],
    completionRates = [],
  } = trendsData;

  // Safe calculation function to avoid infinity
  const safeCalculate = (value1, value2) => {
    if (!value2 || value2 === 0) return 0;
    const result = (value1 / value2) * 100;
    return isFinite(result) ? result : 0;
  };

  // Calculate trends with safe division
  const calculateTrend = (data) => {
    if (!data || data.length < 2) return 0;
    const validData = data.filter(
      (val) => val !== null && val !== undefined && isFinite(val)
    );
    if (validData.length < 2) return 0;

    const recent = validData.slice(-2);
    if (recent[0] === 0) return recent[1] > 0 ? 100 : 0;

    const trend = safeCalculate(recent[1] - recent[0], recent[0]);
    return isFinite(trend) ? trend : 0;
  };

  const tasksTrend = calculateTrend(tasks);
  const completionsTrend = calculateTrend(completions);
  const serviceRequestsTrend = calculateTrend(serviceRequests);
  const completionRateTrend = calculateTrend(completionRates);

  const getTrendIcon = (trend) => {
    return trend >= 0 ? (
      <ArrowUpOutlined
        style={{ color: colorScheme.success, fontSize: "12px" }}
      />
    ) : (
      <ArrowDownOutlined
        style={{ color: colorScheme.danger, fontSize: "12px" }}
      />
    );
  };

  const getTrendColor = (trend) => {
    return trend >= 0 ? colorScheme.success : colorScheme.danger;
  };

  const trendMetrics = [
    {
      key: "tasks",
      title: trans("Tasks Trend"),
      value: trendsData?.trends.tasksTrend,
      trend: tasksTrend,
      color: colorScheme.info,
      description: trans("Trend in task creation"),
      suffix: "%",
      tooltip: "Indicates monthly variation in total tasks created",
    },
    {
      key: "completions",
      title: trans("Completions Trend"),
      value: trendsData?.trends.completionsTrend,
      trend: completionsTrend,
      color: colorScheme.success,
      description: trans("Trend in task completions"),
      suffix: "%",
      tooltip: "Tracks the change in completed tasks over time",
    },
    {
      key: "requests",
      title: trans("Requests Trend"),
      value: trendsData?.trends.serviceRequestsTrend,
      trend: serviceRequestsTrend,
      color: colorScheme.warning,
      description: trans("Trend in service requests"),
      suffix: "%",
      tooltip: "Shows how service request volume changes ",
    },
    {
      key: "rate",
      title: trans("Rate Trend"),
      value: trendsData?.trends.completionRateTrend,
      trend: completionRateTrend,
      color: colorScheme.secondary,
      description: trans("Trend in completion rate"),
      suffix: "%",
      tooltip: "Represents changes in overall performance rate",
    },
  ];

  // Chart options
  const getChartOptions = () => {
    const baseOptions = {
      chart: {
        fontFamily: "'Cairo', sans-serif !important",
        toolbar: { show: false },
        animations: { enabled: true },
      },
      xaxis: {
        categories: months,
        labels: {
          style: { colors: colorScheme.neutral, fontSize: "11px" },
        },
      },
      yaxis: {
        labels: {
          style: { colors: colorScheme.neutral, fontSize: "11px" },
          formatter: (value) => (isFinite(value) ? Math.round(value) : 0),
        },
      },
      grid: {
        borderColor: colorScheme.border,
        strokeDashArray: 3,
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        labels: { colors: colorScheme.dark, fontSize: "12px" },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter: (value) => (isFinite(value) ? Math.round(value) : 0),
        },
      },
    };

    const colors = [
      colorScheme.info,
      colorScheme.success,
      colorScheme.warning,
      colorScheme.secondary,
    ];

    if (chartType === "area") {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: "area", stacked: false },
        colors,
        fill: {
          opacity: 0.6,
          type: "solid",
        },
        stroke: {
          curve: "smooth",
          width: 2,
        },
      };
    } else if (chartType === "bar") {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: "bar" },
        colors,
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "60%",
          },
        },
        dataLabels: { enabled: false },
      };
    } else {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: "line" },
        colors,
        stroke: {
          curve: "smooth",
          width: 3,
        },
        markers: {
          size: 5,
          strokeColors: "#fff",
          strokeWidth: 2,
        },
      };
    }
  };

  const getChartSeries = () => {
    const series = [];

    // Ensure data arrays are valid and finite
    const safeArray = (arr) => arr.map((val) => (isFinite(val) ? val : 0));

    if (selectedMetric === "all" || selectedMetric === "tasks") {
      series.push({
        name: trans("Tasks"),
        data: safeArray(tasks),
      });
    }

    if (selectedMetric === "all" || selectedMetric === "completions") {
      series.push({
        name: trans("Completions"),
        data: safeArray(completions),
      });
    }

    if (selectedMetric === "all" || selectedMetric === "serviceRequests") {
      series.push({
        name: trans("Service Requests"),
        data: safeArray(serviceRequests),
      });
    }

    if (selectedMetric === "all" || selectedMetric === "completionRate") {
      series.push({
        name: trans("Completion Rate") + " (%)",
        data: safeArray(completionRates),
      });
    }

    return series;
  };

  // Show message if no data
  if (!months.length || months.every((month) => !month)) {
    return (
      <Card
        style={{
          borderRadius: "12px",
          border: `1px solid ${colorScheme.border}`,
        }}
      >
        <Alert
          message={trans("No Data Available")}
          description={trans(
            "No performance data found for the selected period"
          )}
          type="info"
          showIcon
          style={{ borderRadius: "6px" }}
        />
      </Card>
    );
  }

  return (
    <div
      style={{
        minHeight: "100%",
        padding: "24px",
        borderRadius: "12px",
        border: `1px solid ${colorScheme.border}`,
      }}
    >
      <SectionTitle
        title={`${trans("Performance Trends")} (5 ${trans("Months")})`}
        subtitle={"Historical performance trends and patterns"}
        imgSrc={`${process.env.PUBLIC_URL}/assets/trend.svg`}
        imgBgColor="#FFF0F6"
      />

      {/* Trend Summary Cards */}
      <Row gutter={[16, 16]}>
        {trendMetrics.map((metric, index) => (
          <Col xs={24} sm={12} lg={6} key={metric.key}>
            <NumbersCard
              title={metric.title}
              value={metric.value}
              suffix={metric.suffix}
              color={metric.color}
              tooltip={metric.tooltip}
              onClick={() => {}}
              popup={false}
            />
          </Col>
        ))}
      </Row>

      {/* Main Chart */}
      <Row style={{ marginTop: "20px" }}>
        <Col span={24}>
          <Tooltip
            title={trans(
              "Tracks monthly trends in tasks, completions, and service requests to evaluate"
            )}
          >
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              bodyStyle={{ padding: "16px" }}
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: "13px", fontWeight: "600" }}>
                    {trans("Performance Chart")}
                  </span>
                </div>
              }
            >
              <ReactApexChart
                options={getChartOptions()}
                series={getChartSeries()}
                type={chartType}
                height={400}
              />
            </Card>
          </Tooltip>
        </Col>
      </Row>

      {/* Performance Insights */}
      <Row style={{ marginTop: "20px" }}>
        <Tooltip
          title={trans(
            "a quick overview of workload and efficiency trends to consider operational performance."
          )}
        >
          <Col span={24}>
            <Card
              style={{
                borderRadius: "12px",
                border: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
              bodyStyle={{ padding: "16px" }}
              title={
                <span style={{ fontSize: "14px", fontWeight: "600" }}>
                  {trans("Performance Insights")}
                </span>
              }
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px",
                      background: tasksTrend >= 0 ? "#EAF4EC88" : "#FFF1F088",
                      borderRadius: "8px",
                      border: `1px solid ${colorScheme.border}`,
                    }}
                  >
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: getTrendColor(tasksTrend),
                        margin: "0 auto 6px",
                      }}
                    />
                    <Text
                      style={{
                        fontSize: "11px",
                        color: colorScheme.dark,
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "3px",
                      }}
                    >
                      {trans("Workload")}
                    </Text>
                    <Text
                      style={{
                        fontSize: "10px",
                        color: getTrendColor(tasksTrend),
                        fontWeight: "500",
                      }}
                    >
                      {tasksTrend >= 0
                        ? trans("Increasing")
                        : trans("Decreasing")}
                    </Text>
                  </div>
                </Col>

                <Col span={12}>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px",
                      background:
                        completionRateTrend >= 0 ? "#EAF4EC88" : "#FFF1F088",
                      borderRadius: "8px",
                      border: `1px solid ${colorScheme.border}`,
                    }}
                  >
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        borderRadius: "50%",
                        backgroundColor: getTrendColor(completionRateTrend),
                        margin: "0 auto 6px",
                      }}
                    />
                    <Text
                      style={{
                        fontSize: "11px",
                        color: colorScheme.dark,
                        fontWeight: "500",
                        display: "block",
                        marginBottom: "3px",
                      }}
                    >
                      {trans("Efficiency")}
                    </Text>
                    <Text
                      style={{
                        fontSize: "10px",
                        color: getTrendColor(completionRateTrend),
                        fontWeight: "500",
                      }}
                    >
                      {completionRateTrend >= 0
                        ? trans("Improving")
                        : trans("Declining")}
                    </Text>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Tooltip>
      </Row>
    </div>
  );
}
